import React, { useState, useMemo } from 'react';
import { Card, Table, Tag, Space, Statistic, Row, Col, Select, Typography, Progress, Tooltip, Button } from 'antd';
import { 
  FileExclamationOutlined, 
  WarningOutlined,
  ReloadOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined
} from '@ant-design/icons';
import { useUninvoicedBonliv } from '../../hooks/useBusinessInsights';
import { useNavigate } from 'react-router-dom';

const { Text } = Typography;

const UninvoicedBonlivCard = () => {
  const [days, setDays] = useState(7);
  const [dateSortOrder, setDateSortOrder] = useState('asc'); // 'asc' or 'desc'
  const { data, loading, error, refetch } = useUninvoicedBonliv(days);
  const navigate = useNavigate();

  const { items = [], summary = {} } = data;

  // Sort items by date based on sortOrder
  const sortedItems = useMemo(() => {
    const sorted = [...items];
    sorted.sort((a, b) => {
      const dateA = new Date(a.DATE_BL);
      const dateB = new Date(b.DATE_BL);
      return dateSortOrder === 'asc' 
        ? dateA - dateB 
        : dateB - dateA;
    });
    return sorted;
  }, [items, dateSortOrder]);

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 'high': return '#ff4d4f';
      case 'medium': return '#faad14';
      case 'low': return '#52c41a';
      default: return '#1890ff';
    }
  };

  const getUrgencyTag = (urgency, daysSinceValidation) => {
    const colors = {
      high: 'error',
      medium: 'warning',
      low: 'success'
    };
    return (
      <Tooltip title={`${daysSinceValidation} jours depuis validation`}>
        <Tag color={colors[urgency]}>
          {daysSinceValidation}j
        </Tag>
      </Tooltip>
    );
  };

  const getStatusTag = (status) => {
    const statusConfig = {
      'EN_PREPARATION': { color: 'orange', label: 'En Préparation' },
      'PREPARE': { color: 'blue', label: 'Préparé' },
      'VALIDE': { color: 'green', label: 'Validé' },
      'TRANSFERE': { color: 'cyan', label: 'Transféré' },
      'GRATUIT': { color: 'gold', label: 'Gratuit' }
    };
    
    const config = statusConfig[status] || { color: 'default', label: status };
    return <Tag color={config.color}>{config.label}</Tag>;
  };

  const columns = [
    {
      title: 'Référence',
      dataIndex: 'REF_BL',
      key: 'REF_BL',
      render: (ref) => (
        <Text 
          strong 
          style={{ cursor: 'pointer', color: '#1890ff' }}
          onClick={() => navigate(`/bonliv?ref=${ref}`)}
        >
          {ref}
        </Text>
      ),
    },
    {
      title: (
        <Space>
          <span>Date</span>
          <Button
            type="text"
            size="small"
            icon={dateSortOrder === 'asc' ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            onClick={() => setDateSortOrder(dateSortOrder === 'asc' ? 'desc' : 'asc')}
            style={{ padding: 0, height: 'auto' }}
          />
        </Space>
      ),
      dataIndex: 'DATE_BL',
      key: 'DATE_BL',
      render: (date) => new Date(date).toLocaleDateString('fr-FR'),
      width: 120,
    },
    {
      title: 'Client',
      key: 'client',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.client?.CLIENT || 'N/A'}</Text>
          <Text type="secondary" style={{ fontSize: '11px' }}>{record.COMPTE || '-'}</Text>
        </Space>
      ),
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (status) => getStatusTag(status),
      width: 120,
      align: 'center',
    },
    {
      title: 'Montant TTC',
      dataIndex: 'MNT_TTC',
      key: 'MNT_TTC',
      render: (amount) => (
        <Text strong style={{ color: '#722ed1' }}>
          {(amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} DH
        </Text>
      ),
      align: 'right',
    },
    {
      title: 'Urgence',
      key: 'urgency',
      render: (_, record) => getUrgencyTag(record.urgency, record.daysSinceValidation),
      width: 80,
      align: 'center',
    },
    {
      title: 'Entrepôt',
      key: 'entrepot',
      render: (_, record) => (
        <Tag>{record.entrepot?.name || record.CODE_ENT || '-'}</Tag>
      ),
      width: 100,
    },
  ];

  const urgencyData = summary.urgencyCounts || { high: 0, medium: 0, low: 0 };
  const totalUrgent = urgencyData.high + urgencyData.medium;

  if (error) {
    return (
      <Card>
        <Text type="danger">Erreur: {error}</Text>
      </Card>
    );
  }

  return (
    <Card 
      title={
        <Space>
          <FileExclamationOutlined style={{ color: '#ff4d4f' }} />
          <span>Bons de Livraison Non Facturés</span>
        </Space>
      }
      extra={
        <Space>
          <Select
            value={days}
            onChange={setDays}
            style={{ width: 140 }}
            options={[
              { value: 7, label: '7 derniers jours' },
              { value: 14, label: '14 derniers jours' },
              { value: 30, label: '30 derniers jours' },
            ]}
          />
          <ReloadOutlined 
            onClick={refetch} 
            style={{ cursor: 'pointer', fontSize: '16px' }} 
            spin={loading}
          />
        </Space>
      }
      loading={loading}
      styles={{ body: { padding: '16px' } }}
    >
      {/* Summary Stats */}
      <Row gutter={[16, 16]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12}>
          <Card 
            size="small" 
            style={{ 
              background: 'linear-gradient(135deg, #ff6b6b 0%, #ff8e8e 100%)',
              border: 'none'
            }}
          >
            <Statistic
              title={<Text style={{ color: 'rgba(255,255,255,0.85)' }}>Total Non Facturés</Text>}
              value={summary.totalCount || 0}
              prefix={<WarningOutlined />}
              valueStyle={{ color: '#fff', fontWeight: 'bold' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12}>
          <Card size="small">
            <Text type="secondary">Répartition par Urgence</Text>
            <div style={{ marginTop: 8 }}>
              <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                <Tooltip title="Urgents (>5 jours)">
                  <Tag color="error">{urgencyData.high} urgents</Tag>
                </Tooltip>
                <Tooltip title="Moyens (3-5 jours)">
                  <Tag color="warning">{urgencyData.medium} moyens</Tag>
                </Tooltip>
                <Tooltip title="Récents (<3 jours)">
                  <Tag color="success">{urgencyData.low} récents</Tag>
                </Tooltip>
              </Space>
              {summary.totalCount > 0 && (
                <Progress
                  percent={Math.round((totalUrgent / summary.totalCount) * 100)}
                  status={totalUrgent > summary.totalCount / 2 ? 'exception' : 'normal'}
                  size="small"
                  style={{ marginTop: 8 }}
                  format={() => `${totalUrgent} à traiter`}
                />
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* Table */}
      <Table
        columns={columns}
        dataSource={sortedItems}
        rowKey="REF_BL"
        size="small"
        pagination={{ 
          pageSize: 10,
          showSizeChanger: true,
          showTotal: (total) => `${total} bon(s) de livraison`
        }}
        scroll={{ x: 700 }}
        rowClassName={(record) => {
          if (record.urgency === 'high') return 'row-urgent';
          if (record.urgency === 'medium') return 'row-warning';
          return '';
        }}
      />

      <style>{`
        .row-urgent {
          background-color: rgba(255, 77, 79, 0.08) !important;
        }
        .row-warning {
          background-color: rgba(250, 173, 20, 0.08) !important;
        }
      `}</style>
    </Card>
  );
};

export default UninvoicedBonlivCard;

